// The consultation workspace and orders (docs/plans/phase1-consultation.md §3.3). Note
// text, diagnosis words, reasons and search text travel only in bodies, never in URLs.
import { queryOptions } from '@tanstack/react-query';
import {
  allergenSearchResultSchema,
  allergyHistorySchema,
  cancelServiceOrderResultSchema,
  closeCheckSchema,
  finishCheckSchema,
  labOrderSchema,
  labTestSearchResultSchema,
  noteHistorySchema,
  placeServiceOrdersResultSchema,
  saveNoteDraftResultSchema,
  SERVICE_ORDER_PATHS,
  serviceItemSearchResultSchema,
  serviceOrderSchema,
  visitOrdersSchema,
  workspaceSchema,
  type AllergyReviewRequest,
  type CorrectDiagnosisRequest,
  type FinishRule,
  type NoteSectionInput,
  type PlaceServiceOrdersRequest,
  type RecordDiagnosisRequest,
  type RecordProblemRequest,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

/** How often the orders panel looks for new results while visible (plan §7.2). */
export const ORDERS_REFRESH_MS = 15_000;

export const workspaceQuery = (visitId: string) =>
  queryOptions({
    queryKey: ['visits', visitId, 'workspace'],
    queryFn: () => api('GET', `/visits/${visitId}/workspace`, { schema: workspaceSchema }),
  });

export function startConsultation(
  visitId: string,
  body: { clientRequestId: string; queueEntryId?: string; servicePointId?: string },
) {
  return api('POST', `/visits/${visitId}/consultations`, { body, schema: workspaceSchema });
}

export function saveNoteDraft(
  consultationId: string,
  body: { sections: NoteSectionInput[]; templateId: string | null },
) {
  return api('PUT', `/consultations/${consultationId}/note/draft`, {
    body,
    schema: saveNoteDraftResultSchema,
  });
}

export function signNote(
  consultationId: string,
  body: { sections: NoteSectionInput[]; templateId: string | null },
) {
  return api('POST', `/consultations/${consultationId}/note/sign`, {
    body,
    schema: workspaceSchema,
  });
}

export function amendNote(noteId: string, body: { sections: NoteSectionInput[]; reason: string }) {
  return api('POST', `/notes/${noteId}/amendments`, { body });
}

export const noteHistoryQuery = (noteId: string) =>
  queryOptions({
    queryKey: ['notes', noteId, 'history'],
    queryFn: () => api('GET', `/notes/${noteId}/history`, { schema: noteHistorySchema }),
  });

export function recordDiagnosis(visitId: string, body: RecordDiagnosisRequest) {
  return api('POST', `/visits/${visitId}/diagnoses`, { body, schema: workspaceSchema });
}

export function correctDiagnosis(id: string, body: CorrectDiagnosisRequest) {
  return api('POST', `/diagnoses/${id}/correct`, { body });
}

export function diagnosisEnteredInError(id: string, reason: string) {
  return api('POST', `/diagnoses/${id}/entered-in-error`, { body: { reason } });
}

export function recordProblem(visitId: string, body: RecordProblemRequest) {
  return api('POST', `/visits/${visitId}/problems`, { body, schema: workspaceSchema });
}

export function updateProblem(
  id: string,
  body: {
    visitId: string;
    expectedVersion: number;
    clinicalStatus?: 'active' | 'inactive' | 'resolved';
    reason: string;
  },
) {
  return api('PATCH', `/problems/${id}`, { body });
}

export function problemEnteredInError(id: string, body: { visitId: string; reason: string }) {
  return api('POST', `/problems/${id}/entered-in-error`, { body });
}

export function reviewAllergy(id: string, body: AllergyReviewRequest) {
  return api('POST', `/allergies/${id}/review`, { body });
}

export const allergyHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: ['allergies', id, 'history'],
    queryFn: () => api('GET', `/allergies/${id}/history`, { schema: allergyHistorySchema }),
  });

export function searchAllergens(query: string) {
  return api('POST', '/allergens/search', { body: { query }, schema: allergenSearchResultSchema });
}

export function finishCheck(consultationId: string) {
  return api('POST', `/consultations/${consultationId}/finish-check`, {
    schema: finishCheckSchema,
  });
}

export function finishConsultation(
  consultationId: string,
  acknowledgements: { ruleId: FinishRule; reason: string }[],
) {
  return api('POST', `/consultations/${consultationId}/finish`, {
    body: { acknowledgements },
    schema: workspaceSchema,
  });
}

export function closeCheck(visitId: string) {
  return api('POST', `/visits/${visitId}/close-check`, { schema: closeCheckSchema });
}

export function closeVisit(visitId: string, reason: string | undefined) {
  return api('POST', `/visits/${visitId}/close`, {
    body: reason === undefined ? {} : { reason },
  });
}

// --- Orders ---------------------------------------------------------------------------------

export const visitOrdersQuery = (visitId: string) =>
  queryOptions({
    queryKey: ['visits', visitId, 'orders'],
    queryFn: () => api('GET', `/visits/${visitId}/orders`, { schema: visitOrdersSchema }),
    refetchInterval: ORDERS_REFRESH_MS,
    refetchIntervalInBackground: false,
  });

export function searchLabTests(visitId: string, query: string) {
  return api('POST', '/lab/tests/search', {
    body: { visitId, query },
    schema: labTestSearchResultSchema,
  });
}

export function placeLabOrders(
  visitId: string,
  body: {
    clientRequestId: string;
    orderingUserId: string;
    tests: {
      serviceItemId: string;
      priority: 'routine' | 'urgent';
      clinicalNotes: string | null;
    }[];
  },
) {
  return api('POST', `/visits/${visitId}/lab-orders`, {
    body: { ...body, source: 'consultation', externalRequester: null },
    schema: z.object({
      orders: z.array(labOrderSchema),
      warnings: z.array(z.object({ serviceItemId: z.uuid(), message: z.string() })),
    }),
  });
}

export function cancelLabOrder(orderId: string, reason: string) {
  return api('POST', `/lab-orders/${orderId}/cancel`, { body: { reason }, schema: labOrderSchema });
}

export type ServiceOrderKind = keyof typeof SERVICE_ORDER_PATHS;

export function searchServiceItems(kind: ServiceOrderKind, visitId: string, query: string) {
  return api('POST', `/${SERVICE_ORDER_PATHS[kind]}/search-items`, {
    body: { visitId, query },
    schema: serviceItemSearchResultSchema,
  });
}

export function placeServiceOrders(
  kind: ServiceOrderKind,
  visitId: string,
  body: PlaceServiceOrdersRequest,
) {
  return api('POST', `/visits/${visitId}/${SERVICE_ORDER_PATHS[kind]}`, {
    body,
    schema: placeServiceOrdersResultSchema,
  });
}

export function performServiceOrder(
  kind: ServiceOrderKind,
  id: string,
  outcomeText: string | null,
) {
  return api('POST', `/${SERVICE_ORDER_PATHS[kind]}/${id}/perform`, {
    body: { outcomeText },
    schema: serviceOrderSchema,
  });
}

export function cancelServiceOrder(kind: ServiceOrderKind, id: string, reason: string) {
  return api('POST', `/${SERVICE_ORDER_PATHS[kind]}/${id}/cancel`, {
    body: { reason },
    schema: cancelServiceOrderResultSchema,
  });
}
